import React, { useMemo, useState, useEffect, useRef, useCallback } from "react";
import { Row, Col, Table, Button, Input } from "reactstrap";
import { useSelector } from "react-redux";
import { createSelector } from "reselect";
import i18n from "../../../i18n";
import { statusMap } from "../../../constants/utils";
import { BookingType, ServiceStatus, ServiceEventType } from "../../../constants/enums";
import { patchUpdateServiceNote, getServiceRiders } from "../../../helpers/api_rombo_helper";
import { getAddressInfo } from "../../../constants/utils";
import GpxViewer from "../../../components/Common/GpxViewer";
import MapStatusMessage from "../../../components/Common/MapStatusMessage";
import AlertSuccess from "../../../components/Alerts/AlertSuccess";
import AlertError from "../../../components/Alerts/AlertError";

const NOTE_EDITABLE_STATUSES = [
  ServiceStatus.UNASSIGNED,
  ServiceStatus.ASSIGNED,
  ServiceStatus.STARTED,
];

const EditableAdditionalNote = React.memo(({ service, onNoteUpdated }) => {
  const currentNote = service.info?.notes?.[0]?.message || "";

  const [isEditing, setIsEditing] = useState(false);
  const [note, setNote] = useState(currentNote);
  const [isLoading, setIsLoading] = useState(false);
  const [showAlertSuccess, setShowAlertSuccess] = useState(false);
  const [showAlertError, setShowAlertError] = useState(false);
  const [alertHeader, setAlertHeader] = useState("");
  const [alertBody, setAlertBody] = useState("");

  const LoginProperties = createSelector(
    (state) => state.Login,
    (Login) => ({ token: Login.token, data: Login.data })
  );
  const { token, data } = useSelector(LoginProperties);

  const isStaff = data?.data?.is_staff === true;
  const canEdit = isStaff && NOTE_EDITABLE_STATUSES.includes(service.status);

  const startEditing = () => {
    setNote(currentNote);
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setNote(currentNote);
    setIsEditing(false);
  };

  const handleSave = async () => {
    if (!note.trim() || isLoading) return;

    setIsLoading(true);
    try {
      await patchUpdateServiceNote(token, service.code, { message: note.trim() });

      setIsEditing(false);
      setAlertHeader(i18n.t("NoteUpdatedSuccessfully"));
      setAlertBody(i18n.t("TheServiceNoteHasBeenUpdated"));
      setShowAlertSuccess(true);

      if (onNoteUpdated) onNoteUpdated(note.trim());
    } catch (error) {
      const status = error?.response?.status;
      setAlertHeader(i18n.t("ErrorUpdatingNoteTitle"));

      if (status === 409) {
        setAlertBody(i18n.t("NoteEditNotAllowed"));
      } else if (status === 422) {
        setAlertBody(i18n.t("NoteValidationError"));
      } else {
        setAlertBody(error?.response?.data?.detail || i18n.t("ErrorUpdatingNote"));
      }
      setShowAlertError(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="note-header">
        <h6 className="detail-subtitle mb-0">{i18n.t("NoteService")}:</h6>
        {canEdit && !isEditing && (
          <button
            type="button"
            className="note-edit-button"
            onClick={startEditing}
            id="edit-service-note"
            aria-label={i18n.t("EditAdditionalNote")}
          >
            <i className="mdi mdi-pencil-outline" />
            {i18n.t("Edit")}
          </button>
        )}
      </div>

      {isEditing ? (
        <>
          <Input
            type="textarea"
            className="form-control mt-1"
            placeholder={i18n.t("WriteYourNoteHere")}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          <div className="note-edit-actions">
            <Button
              className="btn btn-users border"
              type="button"
              onClick={handleSave}
              disabled={!note.trim() || isLoading}
            >
              {isLoading ? i18n.t("Saving") : i18n.t("EditNote")}
            </Button>
            <Button
              className="btn btn-light"
              type="button"
              onClick={cancelEditing}
              disabled={isLoading}
            >
              {i18n.t("Cancel")}
            </Button>
          </div>
        </>
      ) : (
        <p>{currentNote || i18n.t("NoAdditionalNoteMessage")}</p>
      )}

      <AlertSuccess
        showAlertSuccess={showAlertSuccess}
        setShowAlertSuccess={setShowAlertSuccess}
        textHeader={alertHeader}
        textBody={alertBody}
      />
      <AlertError
        showAlertError={showAlertError}
        setShowAlertError={setShowAlertError}
        textHeader={alertHeader}
        textBody={alertBody}
      />
    </>
  );
});

EditableAdditionalNote.displayName = "EditableAdditionalNote";

// --- Timeline helpers (compartidos) -----------------------------------------
// Lógica de presentación del timeline del servicio (iconos/colores/títulos
// i18n + formato de fecha). Se extraen aquí para que puedan reutilizarse tanto
// en el detalle de servicio (Services/DetailService) como en el detalle de
// validación (ServiceValidation), manteniendo exactamente el mismo diseño,
// animación y cadenas i18n. `getEventDetails` depende del `service` (status,
// driver, cancellation_info), por eso se construye vía factory.

// Formatea una fecha/hora al estilo "14 de Octubre de 2026 - 12:20 PM".
export const formatServiceDateTime = (dateStr, timeStr) => {
  if (!dateStr && !timeStr) return "";

  if (typeof dateStr === "string" && dateStr.includes("de")) {
    return dateStr;
  }

  try {
    let combinedDateTime = dateStr || "";
    if (timeStr) {
      const trimmedTime = timeStr.split(".")[0];
      combinedDateTime = dateStr ? `${dateStr}T${trimmedTime}` : trimmedTime;
    }

    const date = new Date(combinedDateTime);
    if (isNaN(date.getTime())) return "";

    const day = date.getDate().toString().padStart(2, "0");
    const month = date.toLocaleString("es-ES", { month: "long" });
    const year = date.getFullYear();

    let hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12;
    const formattedHours = hours.toString().padStart(2, "0");

    return `${day} de ${month.charAt(0).toUpperCase() + month.slice(1)} de ${year} - ${formattedHours}:${minutes} ${ampm}`;
  } catch (e) {
    console.error("Error al formatear la fecha:", e);
    return "";
  }
};

// Devuelve una función getEventDetails(event) ligada a un `service` dado. El
// resultado por evento es { title, description, icon, status } donde status es
// uno de completed | pending | cancelled (clases CSS del timeline).
export const makeGetEventDetails = (service) => (event) => {
  if (!event) {
    return {
      title: i18n.t("UnknownEvent"),
      description: i18n.t("NoDetailsAvailable"),
      icon: "fas fa-question-circle",
      status: "pending",
    };
  }

  if (event.action === "asigment") {
    const isUnassign = event.type === "unassign";
    const driverName = event.name?.replace(/^#/, "") || i18n.t("UnknownDriver");
    const plate = event.plate?.replace(/^#/, "") || i18n.t("UnknownVehicle");

    return {
      title: isUnassign
        ? i18n.t("ServiceRequestUnassigned")
        : i18n.t("ServiceRequestAssigned"),
      description: (
        <>
          <div><strong>{i18n.t("Driver")}:</strong> {driverName}</div>
          <div><strong>{i18n.t("Vehicle")}:</strong> {plate}</div>
        </>
      ),
      icon: isUnassign ? "fas fa-user-minus" : "fas fa-user-plus",
      status: isUnassign ? "cancelled" : "completed",
    };
  }

  if (event.action) {
    switch (event.action) {
      case "request":
        return null;
      case "stop":
        if (event.type === ServiceEventType.ORIGIN_PICKUP) {
          if (![ServiceStatus.STARTED, ServiceStatus.FINISHED].includes(service.status)) {
            return null;
          }
          return {
            title: i18n.t("OriginPickup"),
            description: event.status
              ? (event.status === "PENDING" ? i18n.t("Pending") : i18n.t("Completed"))
              : i18n.t("StatusUnknown"),
            icon: "fas fa-map-marker-alt",
            status: event.status === "PENDING" ? "pending" : "completed",
          };
        } else if (event.type === ServiceEventType.DESTINATION_DROPOFF) {
          if (service.status !== ServiceStatus.FINISHED) {
            return null;
          }
          return {
            title: i18n.t("DestinationDropoff"),
            description: event.status
              ? (event.status === "PENDING" ? i18n.t("Pending") : i18n.t("Completed"))
              : i18n.t("StatusUnknown"),
            icon: "fas fa-flag-checkered",
            status: event.status === "PENDING" ? "pending" : "completed",
          };
        }
        break;
      default:
        return {
          title: event.action || i18n.t("Event"),
          description: event.status || i18n.t("StatusUnknown"),
          icon: "fas fa-circle",
          status: "pending",
        };
    }
  }

  switch (event.type) {
    case ServiceEventType.CREATED:
      return {
        title: i18n.t("ServiceRequestCreated"),
        description: `${i18n.t("ScheduledFor")} ${event.date || i18n.t("DateUnknown")}`,
        icon: "fas fa-calendar-plus",
        status: "completed",
      };
    case ServiceEventType.STARTED:
      return {
        title: i18n.t("ServiceStarted"),
        description: service.driver?.driver_name
          ? (
            <>
              <strong>{i18n.t("Driver")}:</strong> {service.driver.driver_name}
            </>
          )
          : i18n.t("ServiceStarted"),
        icon: "fas fa-play-circle",
        status: "completed",
      };
    case ServiceEventType.STARTED_FROM_SYSTEM:
      return {
        title: i18n.t("ServiceStartedFromSystem"),
        description: event.started_by
          ? (
            <>
              <strong>{i18n.t("StartedBy")}:</strong> {event.started_by}
            </>
          )
          : i18n.t("ServiceStartedFromSystem"),
        icon: "fas fa-user-cog",
        status: "completed",
      };
    case ServiceEventType.COMPLETED:
      return {
        title: i18n.t("ServiceCompleted"),
        description: service.trip?.time
          ? `${i18n.t("Duration")}: ${service.trip.time}`
          : i18n.t("ServiceCompleted"),
        icon: "fas fa-check-circle",
        status: "completed",
      };
    case ServiceEventType.FINISHED_FROM_SYSTEM:
      return {
        title: i18n.t("ServiceFinishedFromSystem"),
        description: (
          <>
            {event.finished_by && (
              <>
                <strong>{i18n.t("FinishedBy")}:</strong> {event.finished_by}
              </>
            )}
            {event.reason && (
              <>
                <br />
                <strong>{i18n.t("Reason")}:</strong> {event.reason}
              </>
            )}
          </>
        ),
        icon: "fas fa-user-times",
        status: "completed",
      };
    case ServiceEventType.CANCELLED:
      return {
        title: i18n.t("ServiceRequestCancelled"),
        description: service.cancellation_info?.reason || i18n.t("NoCancellationReasonProvided"),
        icon: "fas fa-times-circle",
        status: "cancelled",
      };
    default:
      return {
        title: event.type || i18n.t("Event"),
        description: event.description || "",
        icon: "fas fa-circle",
        status: "pending",
      };
  }
};

export const GeneralInfo = React.memo(({ service }) => (
  <>
    <h6 className="detail-subtitle">{i18n.t("Company")}:</h6>
    <p>{service.partner_name}</p>
    <h6 className="detail-subtitle">{i18n.t("Status")}:</h6>
    <p>{i18n.t(statusMap[service.status] || `${service.status}`)}</p>
    {[ServiceStatus.DELETED].includes(service.status) && (
      <>
        <h6 className="detail-subtitle">{i18n.t("CancellationReason")}:</h6>
        <p>{service.cancellation_info?.reason || i18n.t("NoCancellationReason")}</p>
      </>
    )}
    {[ServiceStatus.FINISHED, ServiceStatus.ASSIGNED, ServiceStatus.REJECTED, ServiceStatus.STARTED].includes(service.status) && (
      <>
        <h6 className="detail-subtitle">{i18n.t("DriverData")}:</h6>
        <p className="detail-text">{service.driver?.driver_name}</p>
        <p className="detail-text">{service.vehicle?.plate}</p>
        <p className="detail-text">{`${service.vehicle?.brand} ${service.vehicle?.sub_brand} ${service.vehicle?.model} ${service.vehicle?.color}`}</p>
        {(service.is_route_planned === true || service.is_route_planned === false) && (
          <div className="mt-2">
            <span className={`route-planned-badge ${service.is_route_planned === true ? 'planned' : 'not-planned'}`}>
              <i className={`bx ${service.is_route_planned === true ? 'bx-check-circle' : 'bx-x-circle'} me-1`}></i>
              {service.is_route_planned === true 
                ? i18n.t("RoutePlanned") 
                : i18n.t("RouteNotPlanned")}
            </span>
          </div>
        )}
      </>
    )}
  </>
));

export const ServiceDetails = React.memo(({ service, onNoteUpdated }) => {
  const [selectedPod, setSelectedPod] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [imageError, setImageError] = useState(false);

  const handleViewPod = (pod) => {
    setSelectedPod(pod);
    setImageError(false);
  };

  const openImageModal = () => {
    setShowImageModal(true);
  };

  const closeImageModal = () => {
    setShowImageModal(false);
  };

  const handleImageError = () => {
    setImageError(true);
  };

  return (
    <Row>
      <Col xxl={6} md={6}>
        <h6 className="detail-subtitle">{i18n.t("DateAndHourService")}</h6>
        <p>{(service.trip.scheduled_start_at)}</p>
        {[ServiceStatus.FINISHED, ServiceStatus.ASSIGNED, ServiceStatus.REJECTED].includes(service.status) && (
          <>
            <h6 className="detail-subtitle">{i18n.t("DateAndHourServiceStart")}</h6>
            <p>
              {service?.trip?.started_at
                ? service.trip.started_at
                : i18n.t("ServiceNotStartedMessage")}
            </p>
            <h6 className="detail-subtitle">{i18n.t("DateAndHourServiceEnd")}</h6>
            <p>
              {service?.trip?.ended_at
                ? service.trip.ended_at
                : i18n.t("ServiceNotEndedMessage")}
            </p>
            <h6 className="detail-subtitle">{i18n.t("TypeService")}:</h6>
            <p>{i18n.t(service.type)}</p>
            <h6 className="detail-subtitle">{i18n.t("UnitsRequested")}</h6>
            <p>{service.info?.num_vehicles ?? 1}</p>
          </>
        )}
        {[ServiceStatus.FINISHED].includes(service.status) && (
          <>
            <h6 className="detail-subtitle">{i18n.t("ServiceTime")}</h6>
            <p>{(service.trip.time)}</p>
            <h6 className="detail-subtitle">{i18n.t("ServiceDistance")}</h6>
            <p>{(service.trip.distance)}</p> 
          </>
        )}   
        <EditableAdditionalNote service={service} onNoteUpdated={onNoteUpdated} />
        {service.pods && service.pods.length > 0 && (
          <>
            <h6 className="detail-subtitle mt-4">{i18n.t("ProofsOfService")}</h6>
            <div className="table-responsive">
              <Table bordered className="no-vertical-borders">
                <thead>
                  <tr>
                    <th className="bg-light">{i18n.t("Name")}</th>
                    <th className="bg-light">{i18n.t("Action")}</th>
                  </tr>
                </thead>
                <tbody>
                  {service.pods.map((pod, index) => (
                    <tr key={index}>
                      <td>{i18n.t(pod.type)}</td>
                      <td>
                        <Button 
                          className="bg-users border custom-pod-button"
                          onClick={() => handleViewPod(pod)}
                        >
                          {i18n.t("View")}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </>
        )}
      </Col>
      <Col xxl={6} md={6}>
        <div className="pod-preview-content">
          {selectedPod ? (
            <>
              <div className="pod-title-container">
                <h6 className="detail-subtitle">{i18n.t("ProofsOfService")}</h6>
                <div className="pod-meta">
                  <span className="pod-type">
                    {i18n.t(selectedPod.type)}
                  </span>
                </div>
              </div>
              {imageError ? (
                <div className="image-error-message">
                  <i className="bx bx-error-circle bx-lg text-danger mb-2"></i>
                  <p>{i18n.t("ImageNotAvailable")}</p>
                </div>
              ) : (
                <div className="pod-image-container" onClick={openImageModal}>
                  <img 
                    src={selectedPod.url} 
                    alt={`POD ${selectedPod.type}`} 
                    className="img-fluid pod-image"
                    onError={handleImageError}
                  />
                  <div className="image-overlay">
                    <i className="bx bx-zoom-in"></i> {i18n.t("ClickToZoom")}
                  </div>
                </div>
              )}
            </>
          ) : (
            service.pods && service.pods.length > 0 && (
              <div className="no-pod-selected">
                <i className="bx bx-image-alt bx-lg"></i>
                <p>{i18n.t("SelectPodToPreview")}</p>
              </div>
            )
          )}
        </div>
      </Col>
      {showImageModal && selectedPod && !imageError && (
        <div className="modal-overlay" onClick={closeImageModal}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h5>{i18n.t(selectedPod.type)} - {i18n.t(selectedPod.status)}</h5>
              <button type="button" className="close" onClick={closeImageModal}>
                <span>&times;</span>
              </button>
            </div>
            <div className="modal-body">
              <img 
                src={selectedPod.url} 
                alt={`POD ${selectedPod.type}`} 
                className="img-fluid"
              />
            </div>
            <div className="orange-separator"/>
          </div>
        </div>
      )}
    </Row>
  );
});

const RIDER_POLL_INTERVAL_MS = 12000;

const RiderState = Object.freeze({
  ADDED_IN_ROUTE: "ADDED_IN_ROUTE",
  GOT_OFF: "GOT_OFF",
  ON_BOARD: "ON_BOARD",
  NOT_BOARDED: "NOT_BOARDED",
});

const getRiderState = (rider) => {
  if (rider?.source === "AUTO") return RiderState.ADDED_IN_ROUTE;
  if (rider?.checkout?.at) return RiderState.GOT_OFF;
  if (rider?.checkin?.at) return RiderState.ON_BOARD;
  return RiderState.NOT_BOARDED;
};

const RIDER_STATE_META = {
  [RiderState.ADDED_IN_ROUTE]: { labelKey: "AddedInRoute", className: "rider-badge-added" },
  [RiderState.GOT_OFF]: { labelKey: "GotOff", className: "rider-badge-off" },
  [RiderState.ON_BOARD]: { labelKey: "OnBoard", className: "rider-badge-onboard" },
  [RiderState.NOT_BOARDED]: { labelKey: "NotBoarded", className: "rider-badge-pending" },
};

const formatRiderTime = (isoString, timezone) => {
  if (!isoString) return null;
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return null;
  try {
    return new Intl.DateTimeFormat(i18n.language || "es", {
      hour: "2-digit",
      minute: "2-digit",
      timeZone: timezone || "America/Mexico_City",
    }).format(date);
  } catch (e) {
    return new Intl.DateTimeFormat(i18n.language || "es", {
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "America/Mexico_City",
    }).format(date);
  }
};

const buildServiceStops = (service) => {
  const { origin, destination, scales } = getAddressInfo(service || {});
  const stops = [];
  if (origin) {
    stops.push({ uuid: origin.uuid, label: i18n.t("Origin"), description: origin?.address?.description });
  }
  (scales || []).forEach((scale, index) => {
    stops.push({
      uuid: scale.uuid,
      label: `${i18n.t("Scale")} ${index + 1}`,
      description: scale?.address?.description,
    });
  });
  if (destination) {
    stops.push({ uuid: destination.uuid, label: i18n.t("Destiny"), description: destination?.address?.description });
  }
  return stops;
};

const buildTripStops = (tripStops) => {
  let scaleNumber = 0;
  return [...tripStops]
    .sort((a, b) => a.order - b.order)
    .map((stop) => {
      let label = i18n.t("Scale");
      if (stop.type === "ORIGIN_PICKUP") label = i18n.t("Origin");
      else if (stop.type === "DESTINATION_DROPOFF") label = i18n.t("Destiny");
      else {
        scaleNumber += 1;
        label = `${i18n.t("Scale")} ${scaleNumber}`;
      }
      return { uuid: stop.uuid, label, description: stop.description };
    });
};

const RiderStateBadge = ({ state }) => {
  const meta = RIDER_STATE_META[state] || RIDER_STATE_META[RiderState.NOT_BOARDED];
  return <span className={`rider-badge ${meta.className}`}>{i18n.t(meta.labelKey)}</span>;
};

const getRiderInitials = (name) => {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

const AVATAR_CLASS_BY_STATE = {
  [RiderState.ON_BOARD]: "rider-avatar-onboard",
  [RiderState.NOT_BOARDED]: "rider-avatar-pending",
  [RiderState.GOT_OFF]: "rider-avatar-off",
  [RiderState.ADDED_IN_ROUTE]: "rider-avatar-added",
};

const getCheckStop = (checkPoint, stopsByUuid) =>
  checkPoint?.at && checkPoint?.stop_uuid ? stopsByUuid?.get?.(checkPoint.stop_uuid) || null : null;

const RiderRow = ({ rider, info, passengerNumber, timezone, flagNoCheckout, onViewSignature, stopsByUuid, signatureEnabled = true }) => {
  const state = getRiderState(rider);
  const checkinTime = formatRiderTime(rider?.checkin?.at, timezone);
  const checkoutTime = formatRiderTime(rider?.checkout?.at, timezone);
  const phone = info?.phone;
  const availableTrips = info?.available_trips;
  const hasTrips = typeof availableTrips === "number" && availableTrips >= 0;
  const signatureUrl = signatureEnabled ? rider?.checkout?.signature_url || null : null;

  const checkinStop = getCheckStop(rider?.checkin, stopsByUuid);
  const checkoutStop = getCheckStop(rider?.checkout, stopsByUuid);

  const plannedStopUuid = rider?.checkout?.planned_stop_uuid;
  const gotOffElsewhere =
    !!rider?.checkout?.at &&
    !!plannedStopUuid &&
    rider.checkout.stop_uuid !== plannedStopUuid;
  const plannedStop = gotOffElsewhere ? stopsByUuid?.get?.(plannedStopUuid) : null;
  const plannedStopText = plannedStop?.description || plannedStop?.label || plannedStopUuid;

  return (
    <div className="rider-row">
      <div className={`rider-avatar ${AVATAR_CLASS_BY_STATE[state] || ""}`}>
        {getRiderInitials(rider?.name)}
      </div>
      <div className="rider-row-content">
        <div className="rider-row-main">
          <div className="rider-identity">
            {passengerNumber != null && (
              <span className="rider-number">{`${i18n.t("Passenger")} ${passengerNumber}`}</span>
            )}
            <span className="rider-name">{rider?.name || "-"}</span>
          </div>
          <RiderStateBadge state={state} />
        </div>

        <div className="rider-row-meta">
          {phone && (
            <a className="rider-meta-item rider-meta-link" href={`tel:${phone}`}>
              <i className="mdi mdi-phone-outline" />
              {phone}
            </a>
          )}
          {hasTrips && (
            <span className="rider-meta-item">
              <i className="mdi mdi-ticket-confirmation-outline" />
              {availableTrips} {i18n.t("ServicesAvailable")}
            </span>
          )}
        </div>

        <div className="rider-row-times">
          {checkinTime && (
            <span className="rider-time rider-time-in">
              <i className="mdi mdi-arrow-up-bold-circle-outline" />
              {i18n.t("BoardedAt")} {checkinTime}
              {checkinStop && (
                <span className="rider-time-stop" title={checkinStop.description || undefined}>
                  · {checkinStop.label}
                </span>
              )}
            </span>
          )}
          {checkoutTime && (
            <span className="rider-time rider-time-out">
              <i className="mdi mdi-arrow-down-bold-circle-outline" />
              {i18n.t("GotOffAt")} {checkoutTime}
              {checkoutStop && (
                <span className="rider-time-stop" title={checkoutStop.description || undefined}>
                  · {checkoutStop.label}
                </span>
              )}
            </span>
          )}
          {gotOffElsewhere && (
            <span className="rider-time rider-time-elsewhere">
              <i className="mdi mdi-map-marker-alert-outline" />
              {i18n.t("GotOffAtDifferentStop", { stop: plannedStopText })}
            </span>
          )}
          {flagNoCheckout && (
            <span className="rider-time rider-time-warning">
              <i className="mdi mdi-alert-outline" />
              {i18n.t("FinishedWithoutCheckout")}
            </span>
          )}
        </div>
      </div>
      {signatureUrl && (
        <button
          type="button"
          className="rider-signature-thumb"
          onClick={() => onViewSignature?.(signatureUrl, rider?.name)}
          title={i18n.t("ViewSignature")}
          aria-label={i18n.t("ViewSignature")}
        >
          <span className="rider-signature-label">
            <i className="mdi mdi-draw" /> {i18n.t("Signature")}
          </span>
          <img src={signatureUrl} alt={i18n.t("Signature")} loading="lazy" />
        </button>
      )}
    </div>
  );
};

const PassengersCheckIn = React.memo(({ service }) => {
  const LoginProperties = createSelector(
    (state) => state.Login,
    (Login) => ({ token: Login.token, data: Login.data })
  );
  const { token, data } = useSelector(LoginProperties);
  const isStaff = data?.data?.is_staff === true;

  const [ridersData, setRidersData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const [signatureModal, setSignatureModal] = useState(null);

  const openSignature = useCallback((url, name) => {
    setSignatureModal({ url, name });
  }, []);

  const intervalRef = useRef(null);
  const isMountedRef = useRef(true);

  const code = service?.code;
  const timezone = service?.scheduled_start?.timezone || "America/Mexico_City";

  const fetchRiders = useCallback(async () => {
    if (!token || !code) return;
    try {
      const response = await getServiceRiders(token, code);
      if (!isMountedRef.current) return;
      setRidersData(response);
      setError(false);
    } catch (e) {
      if (!isMountedRef.current) return;
      setError(true);
    } finally {
      if (isMountedRef.current) setIsLoading(false);
    }
  }, [token, code]);

  useEffect(() => {
    isMountedRef.current = true;
    setIsLoading(true);
    fetchRiders();
    return () => {
      isMountedRef.current = false;
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [fetchRiders]);

  useEffect(() => {
    const isActive = ridersData?.trip_status === "ACTIVE";
    if (isActive && !intervalRef.current) {
      intervalRef.current = setInterval(fetchRiders, RIDER_POLL_INTERVAL_MS);
    }
    if (!isActive && intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    return () => {
      if (!isActive && intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [ridersData?.trip_status, fetchRiders]);

  const riders = ridersData?.riders || [];
  const tripStatus = ridersData?.trip_status;
  const isFinished = tripStatus === "FINISHED";
  const isActive = tripStatus === "ACTIVE";
  const checkinDisabled = ridersData?.checkin_enabled === false;
  const signatureEnabled = ridersData?.signature_enabled === true;

  const infoById = useMemo(() => {
    const map = new Map();
    (service?.info?.riders || []).forEach((rider) => {
      if (rider?.id != null) map.set(rider.id, rider);
    });
    return map;
  }, [service]);

  const numberById = useMemo(() => {
    const map = new Map();
    riders.forEach((rider, index) => {
      if (rider?.id != null) map.set(rider.id, index + 1);
    });
    return map;
  }, [riders]);

  const summary = useMemo(() => {
    let onBoard = 0;
    let notBoarded = 0;
    let gotOff = 0;
    riders.forEach((rider) => {
      const state = getRiderState(rider);
      if (state === RiderState.ON_BOARD) onBoard += 1;
      else if (state === RiderState.NOT_BOARDED) notBoarded += 1;
      else if (state === RiderState.GOT_OFF) gotOff += 1;
    });
    return { onBoard, notBoarded, gotOff };
  }, [riders]);

  const groupedByStop = useMemo(() => {
    const stops = ridersData?.stops?.length
      ? buildTripStops(ridersData.stops)
      : buildServiceStops(service);
    const stopIndex = new Map();
    const groups = stops.map((stop) => {
      const group = { ...stop, riders: [] };
      if (stop.uuid) stopIndex.set(stop.uuid, group);
      return group;
    });
    const unassigned = { uuid: null, label: i18n.t("WithoutStop"), description: null, riders: [] };

    riders.forEach((rider) => {
      const stopUuid = rider?.checkout?.stop_uuid;
      const group = stopUuid && stopIndex.get(stopUuid);
      if (group) group.riders.push(rider);
      else unassigned.riders.push(rider);
    });

    const result = groups.filter((group) => group.riders.length > 0);
    if (unassigned.riders.length > 0) result.push(unassigned);
    return result;
  }, [riders, ridersData?.stops, service]);

  const stopsByUuid = useMemo(() => {
    const stops = ridersData?.stops?.length
      ? buildTripStops(ridersData.stops)
      : buildServiceStops(service);
    const map = new Map();
    stops.forEach((stop) => {
      if (stop.uuid) map.set(stop.uuid, stop);
    });
    return map;
  }, [ridersData?.stops, service]);

  if (isLoading && !ridersData) {
    return (
      <div className="riders-status-message">
        <div className="spinner-border text-primary" role="status" />
        <p className="mt-2">{i18n.t("LoadingPassengers")}</p>
      </div>
    );
  }

  if (error && !ridersData) {
    return (
      <div className="riders-status-message">
        <i className="mdi mdi-alert-circle-outline riders-status-icon" />
        <p>{i18n.t("ErrorLoadingPassengers")}</p>
        <Button color="primary" size="sm" onClick={fetchRiders}>
          {i18n.t("Retry")}
        </Button>
      </div>
    );
  }

  if (checkinDisabled) {
    const basicRiders = service?.info?.riders || [];
    return (
      <div className="riders-container riders-basic-container">
        {basicRiders.length > 0 ? (
          <div className="riders-basic-list">
            {basicRiders.map((rider, index) => {
              const availableTrips = rider?.available_trips;
              const hasTrips = typeof availableTrips === "number" && availableTrips >= 0;
              return (
                <div className="rider-basic-row" key={rider.id || index}>
                  <div className="rider-avatar rider-avatar-basic">
                    {getRiderInitials(rider?.name)}
                  </div>
                  <div className="rider-basic-content">
                    <div className="rider-identity">
                      <span className="rider-number">{`${i18n.t("Passenger")} ${index + 1}`}</span>
                      <span className="rider-name">{rider?.name || "-"}</span>
                    </div>
                    <div className="rider-row-meta">
                      {rider?.phone && (
                        <a className="rider-meta-item rider-meta-link" href={`tel:${rider.phone}`}>
                          <i className="mdi mdi-phone-outline" />
                          {rider.phone}
                        </a>
                      )}
                      {hasTrips && (
                        <span className="rider-meta-item">
                          <i className="mdi mdi-ticket-confirmation-outline" />
                          {availableTrips} {i18n.t("ServicesAvailable")}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="riders-status-message">
            <i className="mdi mdi-account-off-outline riders-status-icon" />
            <p>{i18n.t("NoRidersRegistered")}</p>
          </div>
        )}

        {isStaff && (
          <div className="riders-checkin-disabled-note">
            <i className="mdi mdi-account-cancel-outline" />
            <span>{i18n.t("CheckinDisabledForClient")}</span>
          </div>
        )}
      </div>
    );
  }

  if (riders.length === 0) {
    return (
      <div className="riders-status-message">
        <i className="mdi mdi-account-off-outline riders-status-icon" />
        <p>{i18n.t("NoRidersRegistered")}</p>
      </div>
    );
  }

  return (
    <div className="riders-container">
      <div className="riders-summary">
        <div className="riders-summary-item riders-summary-onboard">
          <span className="riders-summary-count">{summary.onBoard}</span>
          <span className="riders-summary-label">{i18n.t("OnBoard")}</span>
        </div>
        <div className="riders-summary-item riders-summary-pending">
          <span className="riders-summary-count">{summary.notBoarded}</span>
          <span className="riders-summary-label">{i18n.t("NotBoarded")}</span>
        </div>
        <div className="riders-summary-item riders-summary-off">
          <span className="riders-summary-count">{summary.gotOff}</span>
          <span className="riders-summary-label">{i18n.t("GotOff")}</span>
        </div>
        {isActive && (
          <span className="riders-live-indicator">
            <span className="riders-live-dot" />
            {i18n.t("Updating")}
          </span>
        )}
      </div>

      {groupedByStop.map((group, index) => (
        <div className="rider-stop-card" key={group.uuid || `stop-${index}`}>
          <div className="rider-stop-header">
            <div className="rider-stop-heading">
              <span className="rider-stop-label">{group.label}</span>
              {group.description && <span className="rider-stop-description">{group.description}</span>}
            </div>
            <span className="rider-stop-count">
              {group.riders.length} {group.riders.length === 1 ? i18n.t("Passenger") : i18n.t("Passengers")}
            </span>
          </div>
          <div className="rider-stop-body">
            {group.riders.map((rider, rIndex) => (
              <RiderRow
                key={rider.id || `${group.uuid}-${rIndex}`}
                rider={rider}
                info={infoById.get(rider.id)}
                passengerNumber={numberById.get(rider.id)}
                timezone={timezone}
                flagNoCheckout={isFinished && getRiderState(rider) === RiderState.ON_BOARD}
                onViewSignature={openSignature}
                stopsByUuid={stopsByUuid}
                signatureEnabled={signatureEnabled}
              />
            ))}
          </div>
        </div>
      ))}
      {signatureModal && (
        <div className="modal-overlay" onClick={() => setSignatureModal(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h5>{i18n.t("Signature")}{signatureModal.name ? ` — ${signatureModal.name}` : ""}</h5>
              <button type="button" className="close" onClick={() => setSignatureModal(null)}>
                <span>&times;</span>
              </button>
            </div>
            <div className="modal-body rider-signature-modal-body">
              <img src={signatureModal.url} alt={i18n.t("Signature")} className="img-fluid" />
            </div>
            <div className="orange-separator" />
          </div>
        </div>
      )}
    </div>
  );
});

export const Passengers = React.memo(({ service }) => {
  const isClosed = service?.type === BookingType.CLOSED;

  if (isClosed) {
    return <PassengersCheckIn service={service} />;
  }

  const riders = service?.info?.riders || [];

  return (
    <>
      {riders.length > 0 ? (
        riders.slice(0, 4).map((rider, index) => (
          <div key={rider.id || index}>
            <h6 className="detail-subtitle">{`${i18n.t("Passenger")} ${index + 1}`}</h6>
            <p className="detail-text">{rider.name}</p>
            <p className="detail-text">{rider?.phone}</p>
            {rider?.available_trips > 0 && (
              <p className="detail-text">
                {rider.available_trips} {i18n.t("ServicesAvailable")}
              </p>
            )}
            <p/>
          </div>
        ))
      ) : (
        <>
          <h6 className="detail-subtitle">{i18n.t("Passenger")}</h6>
          <p>{i18n.t("NoRidersRegistered")}</p>
        </>
      )}
    </>
  );
});

export const Addresses = React.memo(({ service, origin, destination, scales, gpxData, onRetryFetch }) => (
  <Row>
    <Col xxl={6} md={6}>
      <h6 className="detail-subtitle">{i18n.t("Origin")}</h6>
      {origin && <p className="detail-text">{`${origin.address.description}`}</p>}
      <p>{(service.trip.started_at)}</p>
      <h6 className="detail-subtitle">{i18n.t("Destiny")}</h6>
      <p className="detail-text">
        {destination?.address?.description || i18n.t("NoDestiny")}
      </p>
      {[ServiceStatus.FINISHED, ServiceStatus.ASSIGNED, ServiceStatus.REJECTED].includes(service.status) && (
        <p>{(service.trip.ended_at)}</p>
      )}
      <p></p>
      <h6 className="detail-subtitle">{i18n.t("Scales")}</h6>
      {scales && scales.length > 0 ? (
        <div>
          {scales.map((scale, index) => (
            <p key={index}>
              {`${scale.address.description} (${i18n.t("Scale")} ${index + 1})`}
            </p>
          ))}
        </div>
      ) : (
        <p>{i18n.t("NoScalesRegistered")}</p>
      )}
    </Col>
    <Col xxl={6} md={6} xl={6} xs={12} className="gpx-map-container">
      <h6 className="detail-subtitle">{i18n.t("ServiceMap")}</h6>
      {gpxData ? (
        <div style={{ height: '400px', position: 'relative', width: '100%' }}>
          <GpxViewer gpxData={gpxData} />
        </div>
      ) : service.status === BookingType.FINISHED && service.trip?.url_image ? (
        <div style={{ height: '280px', overflow: 'hidden' }}>
          <img 
            src={service.trip.url_image} 
            alt="ServiceMap" 
            style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
          />
        </div>
      ) : (
        <div style={{ 
          height: '400px', 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          backgroundColor: '#F8F9FA',
          borderRadius: '8px'
        }}>
          <MapStatusMessage status={service.status} />
          {onRetryFetch && (
            <Button 
              className="mt-3" 
              color="primary" 
              onClick={onRetryFetch}
            >
              {i18n.t("RetryLoadMap")}
            </Button>
          )}
        </div>
      )}
    </Col>
  </Row>
));

export const Actions = React.memo(({ service, timelineData, getEventDetails, formatDateTime }) => {
  const createdRequestEvent = (timelineData || []).find(
    event => event && event.action === "request" && event.requested_by?.name
  );

  const serviceEvents = useMemo(() => {
    const events = [
      {
        type: ServiceEventType.CREATED,
        date: service.trip?.scheduled_start_at ||
              (service.scheduled_start ? `${service.scheduled_start.date} ${service.scheduled_start.time}` : null),
        create_date: createdRequestEvent?.create_date,
        create_time: createdRequestEvent?.create_time,
        status: "COMPLETED",
        requested_by: createdRequestEvent?.requested_by,
        action_by: createdRequestEvent?.action_by
      },
      ...(timelineData || []).filter(event => event && event.action === "request"),
      ...(timelineData || []).filter(event => event && event.action === "asigment").map(event => ({
        ...event,
        formattedDate: formatDateTime(event.date, event.time)
      })),
      {
        type: ServiceEventType.STARTED,
        date: service.trip?.started_at,
        status: "COMPLETED"
      },
      ...(service.started_from_system ? [{
        type: ServiceEventType.STARTED_FROM_SYSTEM,
        date: service.trip?.started_at ||
              service.trip?.scheduled_start_at ||
              (service.scheduled_start ? `${service.scheduled_start.date} ${service.scheduled_start.time}` : null),
        status: "COMPLETED",
        started_by: service.started_from_system.started_by
      }] : []),
      ...(timelineData || []).filter(event => event && event.action === "stop" && event.type === ServiceEventType.ORIGIN_PICKUP),
      ...(timelineData || []).filter(event => event && event.action === "stop" && event.type === ServiceEventType.DESTINATION_DROPOFF),
      {
        type: ServiceEventType.COMPLETED,
        date: service.trip?.ended_at,
        status: "COMPLETED"
      },
      ...(service.finished_from_system ? [{
        type: ServiceEventType.FINISHED_FROM_SYSTEM,
        formattedDate: formatDateTime(
          service.finished_from_system.finished_at?.date,
          service.finished_from_system.finished_at?.time
        ),
        date: service.finished_from_system.finished_at?.date || service.trip?.ended_at,
        status: "COMPLETED",
        finished_by: service.finished_from_system.finished_by,
        reason: service.finished_from_system.reason
      }] : []),
      ...(service.status === ServiceStatus.DELETED ? [{
        type: ServiceEventType.CANCELLED,
        date: service.trip?.ended_at || service.trip?.started_at || service.trip?.scheduled_start_at,
        status: "CANCELLED",
        cancellation_info: service.cancellation_info
      }] : [])
    ].filter(event => event && (event.date || event.formattedDate || (event.create_date && event.create_time)));

    return events.map(event => {
      const details = getEventDetails(event);
      return details ? { ...event, details } : null;
    }).filter(Boolean);
  }, [service, timelineData, formatDateTime, getEventDetails]);

  return (
    <div className="timeline-container">
      {serviceEvents.length > 0 ? (
        <div className="vertical-timeline timeline-animated" style={{ "--timeline-count": serviceEvents.length }}>
          {serviceEvents.map((event, index) => {
            const requestedBy = event.requested_by?.name;
            const actionBy = event.action_by?.name;

            return (
              <div key={index} className={`timeline-item ${event.details.status}`} style={{ "--i": index }}>
                <div className="timeline-date">
                  {event.formattedDate 
                    ? event.formattedDate
                    : event.type === ServiceEventType.CREATED && event.create_date && event.create_time
                      ? formatDateTime(`${event.create_date} ${event.create_time?.split(".")[0]}`)
                      : formatDateTime(event.date)}
                </div>
                <div className="timeline-icon">
                  <i className={event.details.icon}></i>
                </div>
                <div className="timeline-content">
                  <h4>{event.details.title}</h4>
                  <p>
                    {event.details.description}
                    {event.type === ServiceEventType.CREATED && requestedBy && (
                      <>
                        <br />
                        <strong>{i18n.t("RequestedBy")}:</strong> {requestedBy}
                        <br />
                        <strong>{i18n.t("User")}:</strong> {actionBy}
                      </>
                    )}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <p>{i18n.t("NoTimelineDataAvailable")}</p>
      )}
    </div>
  );
});
